import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '../Button.tsx';
import { Icon } from '../../shell/Icon.tsx';
import styles from './states.module.css';

/* ==========================================================================
   THE LAST STATE: the view itself threw.

   Every other state in this module is a fact about a READ. This one is a fact
   about the console: a component in the route body raised, React unmounted the
   subtree, and without a boundary above it React 18 unmounts the whole root —
   a white viewport, no header, no clock, no way back, and the exception only
   in the developer console where no operator will ever see it.

   It is not hypothetical. One malformed payload from `/api/stats` — a field
   the shell reads without a guard — takes the entire console to a blank page
   today, and a blank page is the one screen from which an operator cannot even
   tell whether the fleet is still there.

   WHAT IT RENDERS, AND WHAT IT DELIBERATELY DOES NOT.

   It cannot keep the route's shape: the code that draws that shape is the code
   that threw. So this is the one place in the console where a failure legibly
   replaces content, and it earns that by being the alternative to nothing at
   all. What it must NOT become is a crash page — no centred illustration, no
   apology, no "something went wrong". It is the same two-part shape every
   stopped panel on the surface takes:

     one --error line, on the row grid, with the state word, the exception's
     own message in mono, and the route it happened on
     one --text-mid line under it saying what is still true
     hatched ground for the rest, and ONE verb

   The verb re-mounts the subtree and re-reads every active query, because the
   overwhelmingly common cause is a shape the view did not expect and a fresh
   read is what fixes it. It is offered once; a second throw from the same
   subtree says so and points at the reload, rather than inviting the operator
   to keep pressing.

   The shell, the banner, the clock and the supply line all survive, because
   this sits INSIDE `Surface` and wraps only the route body.
   ========================================================================== */

interface BoundaryState {
  error: Error | null;
  /** How many times this subtree has thrown since the last manual reset. */
  throws: number;
}

class Boundary extends Component<
  {
    children: ReactNode;
    /** Changing this re-mounts the subtree — a route change clears the fault. */
    resetKey: string;
    render: (error: Error, throws: number, reset: () => void) => ReactNode;
  },
  BoundaryState
> {
  override state: BoundaryState = { error: null, throws: 0 };

  static getDerivedStateFromError(error: Error): Partial<BoundaryState> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    /* The one place the console is allowed to speak to the developer console:
       the component stack is the only thing here an operator cannot use and a
       maintainer cannot do without. */
    console.error('[f3] route render fault', error, info.componentStack);
    this.setState((s) => ({ throws: s.throws + 1 }));
  }

  override componentDidUpdate(prev: { resetKey: string }): void {
    if (prev.resetKey !== this.props.resetKey && this.state.error !== null) {
      this.setState({ error: null, throws: 0 });
    }
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error, throws } = this.state;
    if (error === null) return this.props.children;
    return this.props.render(error, throws, this.reset);
  }
}

/**
 * The rendered fault. A function component so it can reach the query client:
 * re-mounting the subtree over the same cached payload that broke it would
 * throw again on the next frame, so the recovery is "read again, then draw
 * again" and never "draw again".
 */
function RenderFault({
  error,
  throws,
  reset,
  where,
}: {
  error: Error;
  throws: number;
  reset: () => void;
  where: string;
}) {
  const qc = useQueryClient();
  /* Truncated hard, and never the stack: a message is a fact an operator can
     read out to whoever is on call, and a stack is 4KB of noise that pushes
     the one useful line off the row. */
  const message = (error.message || String(error)).split('\n')[0]?.slice(0, 200) ?? 'unknown';
  const again = throws > 1;

  return (
    <div className={styles.crash} role="alert">
      <div className={styles.faultLine} data-tone="error">
        <Icon name="alert-octagon" size={12} />
        <span className={styles.faultWord}>Render fault</span>
        <span className={styles.faultText}>
          {message}
          {'  ·  '}
          {where}
        </span>
      </div>
      <div className={styles.crashRow}>
        <span className={styles.crashLine}>
          {again
            ? 'This view threw again on the same data. The rest of the console is unaffected.'
            : 'This view stopped drawing. Every other surface, and the data behind it, is unaffected.'}
        </span>
        <span className={styles.spacer} />
        {again ? (
          <Button
            size="sm"
            variant="secondary"
            icon="refresh"
            onClick={() => {
              window.location.reload();
            }}
          >
            Reload the console
          </Button>
        ) : (
          <Button
            size="sm"
            variant="secondary"
            icon="refresh"
            onClick={() => {
              void qc.refetchQueries({ type: 'active' });
              reset();
            }}
          >
            Read again
          </Button>
        )}
      </div>
      {/* A DIV, and it must stay the last one in here.

          The shell lays the route body out with
          `[data-surface-body] > * > div:last-of-type { display:flex;
          flex-direction:column; padding:var(--gutter) }` — a structural rule
          that assumes every route is a header div followed by a content div.
          It outranks a plain module class, so whichever div ends this block
          gets stacked and gutter-padded. The hatched ground is the one child
          that does not care, so it takes the hit and the two rows above keep
          their own geometry. */}
      <div className={styles.ground} aria-hidden="true" />
    </div>
  );
}

/** Wraps a route body. Never wraps the shell — the shell is what survives. */
export function RouteBoundary({ children, where }: { children: ReactNode; where: string }) {
  return (
    <Boundary
      resetKey={where}
      render={(error, throws, reset) => (
        <RenderFault error={error} throws={throws} reset={reset} where={where} />
      )}
    >
      {children}
    </Boundary>
  );
}
